/* eslint-disable react/no-unstable-nested-components, no-empty */
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Send, Lock, HeartPulse, Sparkles, Loader2, Mic, MicOff, Volume2, VolumeX } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { toast } from 'sonner';

import { API, getUserPhone } from '../lib/api';
import GiaWellnessHome from '../components/gia/GiaWellnessHome';
import BurnoutCheck from '../components/gia/BurnoutCheck';

const PROMPTS = [
  "I had a difficult outcome today — I just need to talk",
  "I am feeling burned out and exhausted",
  "I cannot sleep after a tough on-call night",
  "Give me a 5-minute guided breathing exercise",
  "I am anxious before a big surgery tomorrow",
  "A patient complaint is weighing on me",
];

export default function GiaPage() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [streamBuffer, setStreamBuffer] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [listening, setListening] = useState(false);
  const endRef = useRef(null);
  const lastAnswerTopRef = useRef(null);
  const recognitionRef = useRef(null);
  const prevStreaming = useRef(false);

  // ====== Indian female voice TTS (Web Speech API) ======
  const [autoSpeak, setAutoSpeak] = useState(() => {
    try { return localStorage.getItem('gia_auto_speak') === '1'; } catch { return false; }
  });
  const [speakingIdx, setSpeakingIdx] = useState(null);
  const indianVoiceRef = useRef(null);
  const voiceQueueRef = useRef([]);       // ranked list of acceptable female voices
  const voiceIdxRef = useRef(0);
  const [voiceLabel, setVoiceLabel] = useState('');
  const [view, setView] = useState('home');

  // Read doctor's first name from signup for personalised welcome / messages
  const firstName = (() => {
    try {
      const saved = JSON.parse(localStorage.getItem('gynovate_signup') || '{}');
      let raw = (saved?.name || '').trim();
      for (const h of ['Dr.', 'Dr', 'Prof.', 'Prof', 'Mr.', 'Mr', 'Ms.', 'Ms', 'Mrs.', 'Mrs']) {
        if (raw.toLowerCase().startsWith(h.toLowerCase() + ' ')) { raw = raw.slice(h.length + 1); break; }
      }
      const parts = raw.split(/\s+/).filter(Boolean);
      return parts[0] || '';
    } catch { return ''; }
  })();

  useEffect(() => {
    if (typeof window === 'undefined' || !window.speechSynthesis) return;
    const pickVoice = () => {
      const voices = window.speechSynthesis.getVoices();
      if (!voices?.length) return;
      // Strongly prefer Google voices (best natural quality on Chrome), female English.
      const score = (v) => {
        const lang = (v.lang || '').toLowerCase();
        const name = (v.name || '').toLowerCase();
        let s = 0;
        const isGoogle = /google/.test(name);
        // ⭐ Google voices get a huge bump — best quality natural TTS
        if (isGoogle) s += 100;
        if (isGoogle && /female/.test(name)) s += 30;
        if (isGoogle && lang.startsWith('en-gb')) s += 25;
        if (isGoogle && lang.startsWith('en-us')) s += 20;
        if (isGoogle && lang.startsWith('en-in')) s += 15;
        // Non-Google premium female voices as fallback
        if (!isGoogle && /samantha|aria|jenny|zira|libby|sonia|nora|emma|olivia/.test(name)) s += 50;
        if (lang.startsWith('en-gb')) s += 10;
        if (lang.startsWith('en-us')) s += 8;
        if (lang.startsWith('en-in')) s += 6;
        if (lang.startsWith('en')) s += 3;
        if (/female|woman|girl|veena|lekha|raveena|aditi/.test(name)) s += 15;
        if (/male|man|guy/.test(name) && !/female|woman/.test(name)) s -= 40;
        return s;
      };
      const ranked = voices
        .filter(v => (v.lang || '').toLowerCase().startsWith('en') || (v.lang || '').toLowerCase().startsWith('hi'))
        .sort((a, b) => score(b) - score(a));
      voiceQueueRef.current = ranked.length ? ranked : voices;
      voiceIdxRef.current = 0;
      indianVoiceRef.current = voiceQueueRef.current[0] || null;
      setVoiceLabel(indianVoiceRef.current ? indianVoiceRef.current.name.split(' ')[0] : '');
    };
    pickVoice();
    window.speechSynthesis.onvoiceschanged = pickVoice;
    return () => { try { window.speechSynthesis.cancel(); } catch { } };
  }, []);

  // Lets the doctor cycle to the next available voice
  const cycleVoice = () => {
    const queue = voiceQueueRef.current;
    if (!queue?.length) return;
    voiceIdxRef.current = (voiceIdxRef.current + 1) % queue.length;
    indianVoiceRef.current = queue[voiceIdxRef.current];
    setVoiceLabel(indianVoiceRef.current?.name?.split(' ')[0] || '');
    try { window.speechSynthesis.cancel(); } catch { }
    // Tiny preview so the doctor hears the new voice immediately
    const u = new SpeechSynthesisUtterance("Hi, this is Gia. How does this voice feel?");
    u.voice = indianVoiceRef.current;
    u.lang = indianVoiceRef.current?.lang || 'en-US';
    u.rate = 1.05;
    u.pitch = 1.0;
    window.speechSynthesis.speak(u);
  };

  // Strip markdown and shape for natural conversational speech
  const stripMarkdown = (md) =>
    String(md || '')
      .replace(/```[\s\S]*?```/g, '')
      .replace(/`([^`]+)`/g, '$1')
      .replace(/!\[[^\]]*]\([^)]+\)/g, '')
      .replace(/\[([^\]]+)]\([^)]+\)/g, '$1')
      .replace(/[*_~#>]/g, '')
      .replace(/^\s*[-•]\s+/gm, '')
      // Convert paragraph breaks into a natural breath pause
      .replace(/\n{2,}/g, '... ')
      .replace(/\n+/g, ', ')
      // Tighten whitespace and dash usage so it sounds conversational
      .replace(/\s*—\s*/g, ', ')
      .replace(/\s+/g, ' ')
      .trim();

  // Split into sentence chunks so each gets natural conversational prosody + pauses
  const chunkForSpeech = (text) =>
    text
      .split(/(?<=[.!?])\s+/)
      .map(s => s.trim())
      .filter(Boolean);

  const speak = (text, idx) => {
    if (!window.speechSynthesis) {
      toast.error('Voice playback not supported in this browser');
      return;
    }
    window.speechSynthesis.cancel();
    const clean = stripMarkdown(text);
    if (!clean) return;
    const chunks = chunkForSpeech(clean);
    setSpeakingIdx(idx);
    chunks.forEach((chunk, ci) => {
      const utter = new SpeechSynthesisUtterance(chunk);
      if (indianVoiceRef.current) utter.voice = indianVoiceRef.current;
      utter.lang = indianVoiceRef.current?.lang || 'en-IN';
      utter.rate = 1.05;   // gentle conversational pace
      utter.pitch = 1.0;   // natural pitch
      utter.volume = 1;
      if (ci === chunks.length - 1) {
        utter.onend = () => setSpeakingIdx(null);
        utter.onerror = () => setSpeakingIdx(null);
      }
      window.speechSynthesis.speak(utter);
    });
  };

  const stopSpeaking = () => {
    try { window.speechSynthesis.cancel(); } catch { /* noop */ }
    setSpeakingIdx(null);
  };

  const toggleAutoSpeak = () => {
    setAutoSpeak(v => {
      const nv = !v;
      try { localStorage.setItem('gia_auto_speak', nv ? '1' : '0'); } catch { /* noop */ }
      if (!nv) stopSpeaking();
      toast.info(nv ? 'Gia will speak her replies' : 'Gia will stay silent');
      return nv;
    });
  };

  // Auto-speak the latest assistant message when streaming finishes
  useEffect(() => {
    if (!autoSpeak) return;
    if (prevStreaming.current && !streaming && messages.length) {
      const last = messages[messages.length - 1];
      if (last?.role === 'assistant') {
        // Slight delay so the user sees the message before audio starts
        setTimeout(() => speak(last.content, messages.length - 1), 250);
      }
    }
    // intentionally left without speak/stopSpeaking deps — they're stable refs
  }, [streaming, messages, autoSpeak]);
  // ====== end TTS ======

  useEffect(() => {
    // When Gia's reply completes (streaming: true → false), scroll to the TOP of that answer
    if (prevStreaming.current && !streaming) {
      // Small timeout so the final rendered Gia message is in the DOM
      setTimeout(() => {
        lastAnswerTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 80);
    }
    prevStreaming.current = streaming;
  }, [streaming]);

  useEffect(() => {
    // During streaming keep the input visible at bottom; once conversation exists, gentle follow
    if (messages.length === 0 && !streamBuffer) return;
    if (streaming) {
      endRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, streamBuffer, streaming]);

  useEffect(() => {
    // Scroll page to top on Gia open — doctor lands on welcome view
    window.scrollTo({ top: 0, behavior: 'auto' });
    try {
      const saved = JSON.parse(localStorage.getItem('gynovate_signup') || 'null');
      if (!saved?.phone) { navigate('/signup'); return; }
      // Gate: if access expired (legacy state) → bounce back to /chat
      fetch(`${API}/api/plan/${saved.phone}`)
        .then(r => r.json())
        .then(d => {
          if (d.access === 'expired') {
            toast.error('Your access has ended. Please subscribe to continue.');
            navigate('/chat');
          }
        })
        .catch(() => { /* soft fail — keep Gia accessible */ });
    } catch {
      navigate('/signup');
    }
  }, [navigate]);

  const ensureSession = async () => {
    if (sessionId) return sessionId;
    const res = await axios.post(`${API}/api/sessions`, {
      title: 'Gia Wellness Session',
      mode: 'Talk to Gia',
      phone: getUserPhone(),
    });
    const id = res.data.id;
    setSessionId(id);
    return id;
  };

  const send = async (text) => {
    const trimmed = (text || input).trim();
    if (!trimmed || streaming) return;
    setInput('');
    setMessages(m => [...m, { role: 'user', content: trimmed }]);
    setStreaming(true);
    setStreamBuffer('');

    try {
      const sid = await ensureSession();
      // Read doctor's name from signup so Gia can address them personally
      let doctorName = '';
      try {
        const saved = JSON.parse(localStorage.getItem('gynovate_signup') || '{}');
        doctorName = (saved?.name || '').trim();
      } catch { /* noop */ }
      const res = await fetch(`${API}/api/sessions/${sid}/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: trimmed,
          mode: 'Talk to Gia',
          language: 'English',
          doctor_name: doctorName || undefined,
        }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      let full = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split('\n');
        buf = lines.pop() || '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const d = JSON.parse(line.slice(6));
            if (d.type === 'chunk') {
              full += d.content;
              setStreamBuffer(full);
            }
          } catch {}
        }
      }
      setMessages(m => [...m, { role: 'assistant', content: full }]);
      setStreamBuffer('');
    } catch (e) {
      setMessages(m => [...m, { role: 'assistant', content: 'Sorry, I could not respond right now. Please try again in a moment.' }]);
    } finally {
      setStreaming(false);
    }
  };

  const toggleVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      toast.error('Voice input is not supported in this browser. Try Chrome or Safari.');
      return;
    }

    if (listening && recognitionRef.current) {
      recognitionRef.current.stop();
      return;
    }

    const rec = new SR();
    rec.lang = 'en-IN';
    rec.interimResults = true;
    rec.continuous = true;

    const base = input ? input.trim() + ' ' : '';
    let finalText = '';
    rec.onstart = () => setListening(true);
    rec.onerror = (e) => {
      setListening(false);
      toast.error(e.error === 'not-allowed' ? 'Please allow microphone access' : 'Could not capture voice');
    };
    rec.onend = () => {
      setListening(false);
      recognitionRef.current = null;
    };
    rec.onresult = (event) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; ++i) {
        const t = event.results[i][0].transcript;
        if (event.results[i].isFinal) finalText += t.trim() + ' ';
        else interim += t;
      }
      setInput((base + finalText + interim).replace(/\s+/g, ' ').trimStart());
    };

    recognitionRef.current = rec;
    try { rec.start(); } catch { setListening(false); }
  };

  return (
    <div className="min-h-screen relative overflow-hidden"
         style={{ background: 'radial-gradient(1200px circle at 20% 0%, rgba(192,132,252,0.45), transparent 45%), radial-gradient(900px circle at 80% 100%, rgba(244,114,182,0.30), transparent 45%), radial-gradient(700px circle at 50% 50%, rgba(192,18,60,0.10), transparent 50%), linear-gradient(160deg, #0c0820 0%, #1f1232 55%, #150b25 100%)' }}>
      <div className="pointer-events-none absolute inset-0 opacity-[0.035] mix-blend-overlay z-0"
           style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")" }} />

      <header className="relative z-10 flex items-center justify-between px-5 py-4 border-b border-white/5 backdrop-blur-xl"
              style={{ background: 'rgba(10,7,18,0.55)' }}>
        <button
          onClick={() => (view === 'home' ? navigate('/chat') : setView('home'))}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-white/70 hover:text-white hover:bg-white/5 transition whitespace-nowrap text-xs sm:text-sm"
          data-testid="gia-back-btn"
        >
          <ArrowLeft className="w-4 h-4 shrink-0" /> {view === 'home' ? 'Back' : 'Gia Home'}
        </button>
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-full flex items-center justify-center shadow-lg"
               style={{ background: 'linear-gradient(135deg,#a855f7,#7c3aed)' }}>
            <HeartPulse className="w-5 h-5 text-white" />
          </div>
          <div className="leading-tight">
            <p className="text-sm font-black text-white tracking-tight">Gia</p>
            <p className="text-[10px] text-white/50 uppercase tracking-widest font-semibold">Mental Wellness · For Doctors</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={cycleVoice}
            className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border border-white/15 bg-white/5 text-white/70 hover:text-white hover:bg-white/10 transition-colors"
            data-testid="gia-cycle-voice-btn"
            title={voiceLabel ? `Voice: ${voiceLabel} — tap to try another` : 'Try another voice'}
          >
            <Volume2 className="w-3 h-3" />
            {voiceLabel ? voiceLabel.slice(0, 10) : 'Voice'}
          </button>
          <button
            onClick={toggleAutoSpeak}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border transition-colors ${
              autoSpeak
                ? 'bg-purple-500/20 border-purple-400/50 text-purple-200'
                : 'bg-white/5 border-white/15 text-white/60 hover:text-white/90'
            }`}
            data-testid="gia-auto-speak-toggle"
            title={autoSpeak ? 'Auto-voice ON' : 'Auto-voice OFF — tap to enable'}
          >
            {autoSpeak ? <Volume2 className="w-3 h-3" /> : <VolumeX className="w-3 h-3" />}
            {autoSpeak ? 'Voice On' : 'Voice Off'}
          </button>
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-emerald-500/30 bg-emerald-500/10">
            <Lock className="w-3 h-3 text-emerald-400" />
            <span className="text-[10px] font-bold text-emerald-300 uppercase tracking-wider">Private</span>
          </div>
        </div>
      </header>

      {view === 'home' && (
        <GiaWellnessHome
          firstName={firstName}
          onTalk={(prefill) => { setView('chat'); if (prefill) setTimeout(() => send(prefill), 80); }}
          onBurnout={() => setView('burnout')}
        />
      )}

      {view === 'burnout' && (
        <BurnoutCheck
          onHome={() => setView('home')}
          onTalk={(msg) => { setView('chat'); setTimeout(() => send(msg), 80); }}
        />
      )}

      {view === 'chat' && (
      <main className="relative z-10 max-w-2xl mx-auto px-5 pb-48 pt-8">
        {messages.length === 0 && !streaming && (
          <div data-testid="gia-welcome">
            <div className="flex items-center gap-2 mb-3 text-purple-300 text-xs font-bold uppercase tracking-widest">
              <Sparkles className="w-3.5 h-3.5" /> Confidential Companion · For Doctors
            </div>
            <h1 className="text-4xl sm:text-5xl font-black text-white leading-[1.05] mb-5 tracking-tight">
              {firstName ? <>Hey {firstName} —<br/></> : <>You&apos;re carrying a lot.<br/></>}
              <span style={{ background: 'linear-gradient(120deg,#c4b5fd,#f0abfc,#f472b6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                {firstName ? 'let\u2019s put some of it down.' : 'Let\u2019s put some of it down.'}
              </span>
            </h1>
            <p className="text-white/70 text-sm leading-relaxed max-w-md mb-10">
              I&apos;m Gia — a confidential companion built for doctors{firstName ? `, ${firstName}` : ''}. Everything you share here stays only in this conversation. Nothing is saved unless you ask me to.
            </p>

            <p className="text-xs font-bold text-white/50 uppercase tracking-widest mb-3">Start with</p>
            <div className="space-y-2">
              {PROMPTS.map((p, i) => (
                <button
                  key={i}
                  onClick={() => send(p)}
                  className="group block w-full text-left px-4 py-3.5 rounded-2xl border transition-all hover:translate-x-1"
                  style={{
                    background: 'linear-gradient(92deg, rgba(255,255,255,0.03), rgba(139,92,246,0.06))',
                    borderColor: 'rgba(255,255,255,0.08)',
                  }}
                  data-testid={`gia-prompt-${i}`}
                >
                  <span className="text-sm text-white/90 group-hover:text-white transition">{p}</span>
                  <span className="float-right text-purple-400/60 group-hover:text-purple-300">→</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-5">
          {messages.map((m, i) => {
            const isLastAssistant = m.role === 'assistant' && i === messages.length - 1;
            return (
            <div
              key={i}
              ref={isLastAssistant ? lastAnswerTopRef : null}
              className={m.role === 'user' ? 'flex justify-end' : 'scroll-mt-24'}
              data-testid={`gia-msg-${i}`}
            >
              {m.role === 'user' ? (
                <div className="max-w-[80%] px-4 py-3 rounded-2xl rounded-br-md shadow-lg"
                     style={{ background: 'linear-gradient(135deg,#c4b5fd,#a78bfa)', color: '#1a0f25' }}>
                  <p className="text-sm leading-relaxed font-medium">{m.content}</p>
                </div>
              ) : (
                <div className="max-w-[94%]">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center"
                         style={{ background: 'linear-gradient(135deg,#a855f7,#7c3aed)' }}>
                      <HeartPulse className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-xs font-bold text-purple-300 tracking-wide">Gia</span>
                    <button
                      onClick={() => (speakingIdx === i ? stopSpeaking() : speak(m.content, i))}
                      className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border border-purple-500/30 bg-purple-500/10 text-purple-200 hover:bg-purple-500/20 transition-colors"
                      data-testid={`gia-speak-btn-${i}`}
                      title={speakingIdx === i ? 'Stop' : 'Listen in Indian voice'}
                    >
                      {speakingIdx === i ? (
                        <><VolumeX className="w-3 h-3" /> Stop</>
                      ) : (
                        <><Volume2 className="w-3 h-3" /> Listen</>
                      )}
                    </button>
                    <button
                      onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(stripMarkdown(m.content) + '\n\n— via Gia · Gynovate AI/SI')}`, '_blank')}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border border-emerald-500/30 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20 transition-colors"
                      data-testid={`gia-whatsapp-btn-${i}`}
                      title="Share on WhatsApp"
                    >
                      ⇪ WhatsApp
                    </button>
                  </div>
                  <div className="pl-8 text-[15px] leading-[1.75] font-normal" style={{ color: '#f5f3ff' }}>
                    <ReactMarkdown
                      components={{
                        p: ({ node, ...props }) => <p style={{ color: '#f5f3ff', marginBottom: '14px' }} {...props} />,
                        strong: ({ node, ...props }) => <strong style={{ color: '#e9d5ff', fontWeight: 700 }} {...props} />,
                        em: ({ node, ...props }) => <em style={{ color: '#c4b5fd' }} {...props} />,
                        li: ({ node, ...props }) => <li style={{ color: '#f5f3ff', marginBottom: '6px' }} {...props} />,
                        ul: ({ node, ...props }) => <ul style={{ paddingLeft: '20px', marginBottom: '12px' }} {...props} />,
                        ol: ({ node, ...props }) => <ol style={{ paddingLeft: '20px', marginBottom: '12px' }} {...props} />,
                        h1: ({ node, ...props }) => <h2 style={{ color: '#ffffff', fontWeight: 700, fontSize: '18px', marginTop: '16px', marginBottom: '8px' }} {...props} />,
                        h2: ({ node, ...props }) => <h3 style={{ color: '#ffffff', fontWeight: 700, fontSize: '16px', marginTop: '14px', marginBottom: '6px' }} {...props} />,
                        h3: ({ node, ...props }) => <h4 style={{ color: '#ede9fe', fontWeight: 600, fontSize: '15px', marginTop: '10px', marginBottom: '4px' }} {...props} />,
                        code: ({ node, ...props }) => <code style={{ color: '#fbcfe8', background: 'rgba(255,255,255,0.08)', padding: '1px 6px', borderRadius: '4px' }} {...props} />,
                        blockquote: ({ node, ...props }) => <blockquote style={{ color: '#ddd6fe', borderLeft: '3px solid #a855f7', paddingLeft: '12px', fontStyle: 'italic', margin: '8px 0' }} {...props} />,
                      }}
                    >
                      {m.content}
                    </ReactMarkdown>
                  </div>
                </div>
              )}
            </div>
          );
          })}

          {streaming && (
            <div data-testid="gia-streaming">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 rounded-full flex items-center justify-center"
                     style={{ background: 'linear-gradient(135deg,#a855f7,#7c3aed)' }}>
                  <HeartPulse className="w-3 h-3 text-white animate-pulse" />
                </div>
                <span className="text-xs font-bold text-purple-300 tracking-wide">Gia is listening…</span>
              </div>
              <div className="pl-8">
                {streamBuffer ? (
                  <div className="text-[15px] leading-[1.75]" style={{ color: '#f5f3ff' }}>
                    <ReactMarkdown
                      components={{
                        p: ({ node, ...props }) => <p style={{ color: '#f5f3ff', marginBottom: '14px' }} {...props} />,
                        strong: ({ node, ...props }) => <strong style={{ color: '#e9d5ff', fontWeight: 700 }} {...props} />,
                      }}
                    >{streamBuffer}</ReactMarkdown>
                  </div>
                ) : (
                  <div className="space-y-2 animate-pulse">
                    <div className="h-3 rounded bg-white/10 w-[85%]" />
                    <div className="h-3 rounded bg-white/10 w-[65%]" />
                    <div className="h-3 rounded bg-white/10 w-[75%]" />
                  </div>
                )}
              </div>
            </div>
          )}

          <div ref={endRef} />
        </div>
      </main>
      )}

      {view === 'chat' && (
      <div className="fixed bottom-0 left-0 right-0 z-20 p-4 backdrop-blur-xl"
           style={{ background: 'linear-gradient(to top, #0a0712 60%, rgba(10,7,18,0.6))' }}>
        <div className="max-w-2xl mx-auto">
          <form onSubmit={(e) => { e.preventDefault(); send(); }}
                className="flex items-end gap-2 p-2 rounded-2xl border shadow-2xl"
                style={{ background: 'rgba(255,255,255,0.04)', borderColor: 'rgba(255,255,255,0.1)' }}
                data-testid="gia-form">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
              }}
              rows={1}
              placeholder={listening ? "Listening… speak now" : "What's on your mind? I'm listening…"}
              className="flex-1 bg-transparent text-white placeholder-white/35 text-sm px-3 py-2.5 focus:outline-none resize-none max-h-32"
              data-testid="gia-input"
              disabled={streaming}
            />
            <button
              type="button"
              onClick={toggleVoice}
              disabled={streaming}
              className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center text-white transition ${listening ? 'animate-pulse' : ''}`}
              style={{ background: listening ? '#dc2626' : 'rgba(255,255,255,0.08)' }}
              data-testid="gia-voice-btn"
              title={listening ? 'Stop recording' : 'Speak to Gia'}
            >
              {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            </button>
            <button
              type="submit"
              disabled={!input.trim() || streaming}
              className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center text-white transition disabled:opacity-30"
              style={{ background: 'linear-gradient(135deg,#a855f7,#7c3aed)' }}
              data-testid="gia-send"
            >
              {streaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </form>
          <p className="text-[10px] text-white/45 text-center mt-2.5">
            🔒 This conversation is private · nothing is saved · auto-deleted in 24h
          </p>
        </div>
      </div>
      )}
    </div>
  );
}
